/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { removeClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import {
  SearchBox,
  SearchRows,
  SearchCols,
  SearchBoxRow,
  InputBox,
} from 'components/layouts/SearchBox';
import { SubTitleGroup, SubTitleLayout, ControlBtnGroup } from 'components/layouts/ContentLayout';
import { IpCheckResultCondition, IpCheckResult } from 'models/ip/IpCheckResult';
import { findCheckResult } from 'apis/ip/IpCheckResult';
import CheckResultParaPointModal from './modals/CheckResultParaPointPopUp';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { EquipmentGroupPopUp } from 'pages/common/popup/EquipmentGroupPopUp';
import { ComboBox } from 'components/selects/ComboBox';
import { ParaItemPopUp } from 'pages/ip/popup/ParaItemPopUp';
import PartnerUserModal from 'components/modals/common/PartnerUserModal';
import PartnerModal from 'components/modals/common/PartnerModal';
import { ElmEmpPopup } from 'pages/common/ElmEmpPopup';
import { EquipmentGroupCondition } from 'models/common/popup/EquipmentGroup';
import { ParaItemCondition } from 'models/ip/popup/ParaItem';
import CustomGrid from 'components/grids/CustomGrid';
import FileUploadPopUp from '../../common/components/FileUploadPopUp';
import { getExcelFileName } from 'utils/ExcelUtil';
import { ExcpPartner } from 'models/admin/ExcpPartner';
import { CommonUtil } from 'utils/CommonUtil';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import GridSettingButtons from 'components/grids/GridSettingButtons';

interface CheckResultPageLocationState {
  ipCheckPlanNo?: string;
  condition?: any;
  pageNo?: number;
}

interface DisplayCondition {
  paraItemNm?: string; // 파라미터 항목명
  ptnrNm?: string; // 협력사코드명
  makerInspectorNm?: string; // maker점검자명
  ensolInspectorNm?: string; // 엔솔점검자명
}

const CheckResultPage = () => {
  const gridRef = useRef<any>();
  const gridRef2 = useRef<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);
  const { t } = useTranslation();
  const locationState: CheckResultPageLocationState = useLocation().state;
  const [isOpenSearchBox, setOpenSearchBox] = useState<boolean>(false);
  const [rowData, setRowData] = useState<IpCheckResult[]>([]);

  // 측정개소 Modal
  const [isOpenCheckResultParaPointModal, setIsOpenCheckResultParaPointModal] =
    useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<IpCheckResultCondition>({});
  const [ipCheckResultParamCondition, setIpCheckResultParamCondition] =
    useState<IpCheckResultCondition>({
      ipCheckPlanNo: locationState?.ipCheckPlanNo || '',
    });
  const [isOpenEquipmentGroupPopUp, setOpenEequipmentGroupPopUp] = useState<boolean>(false);
  const [isOpenParaItemPopUp, setOpenParaItemPopUp] = useState<boolean>(false);
  const [ipStatusCodes, setIpStatusCodes] = useState<Code[]>([]);
  const [isIsptTpCd, setIsptTpCd] = useState<Code[]>([]);
  const [copCodes, setCopCodes] = useState<Code[]>([]);

  const [isOpenPartnerUserModal, setOpenPartnerUserModal] = useState<boolean>(false);
  const [isOpenPartnerModal, setIsOpenPartnerModal] = useState<boolean>(false);

  const [isOpenElmEmpPopup, setOpenElmEmpPopup] = useState<boolean>(false);
  const [userData, setUserData] = useState<any>('');
  const [displayCondition, setDisplayCondition] = useState<DisplayCondition>({});
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>({});

  useEffect(() => {
    getCommonCode();
    handleSearch(ipCheckResultParamCondition);
  }, []);

  useEffect(() => {
    if (locationState?.ipCheckPlanNo) {
      const condition = {
        ipCheckPlanNo: locationState?.ipCheckPlanNo || ipCheckResultParamCondition.ipCheckPlanNo,
      };
      setIpCheckResultParamCondition(condition);
      handleSearch(condition);
    }
  }, [locationState]);

  useEffect(() => {
    if (userData != null && userData != '') {
      const userInfo = userData.userInfo[0] || {};
      setIpCheckResultParamCondition({
        ...ipCheckResultParamCondition,
        ensolInspector: userInfo.userId,
      });
      setDisplayCondition({
        ...displayCondition,
        ensolInspectorNm: userInfo.empNm,
      });
    }
  }, [userData]);

  const getCommonCode = async () => {
    const result = await getCommonCodeNames('IP_STATUS_CODE');
    const istpList = await getCommonCodeNames('IP_ISPT_TP_CD');
    setIpStatusCodes(result.filter((code) => code.cmnCd !== 'CANCEL'));
    setCopCodes(await getCommonCodeNames('ELM_COP_CD'));
    setIsptTpCd(istpList);
  };

  const handleSearch = async (condition: IpCheckResultCondition) => {
    await findCheckResult(condition).then((response) => {
      setRowData(response || []);
    });
  };

  const handleParamConditionChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    setIpCheckResultParamCondition({
      ...ipCheckResultParamCondition,
      [e.target.name]: e.target.value,
    });
  };

  const handleDisplayConditionChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const name = e.target.name;
    setDisplayCondition({
      ...displayCondition,
      [name]: e.target.value,
    });

    setIpCheckResultParamCondition({
      ...ipCheckResultParamCondition,
      [name.replace('Nm', '')]: '',
    });
  };

  const handleParaPointTypeNmClick = (item) => {
    setSelectedRow({
      ipCheckPlanNo: item.ipCheckPlanNo,
      ipCheckTargetSeqId: item.ipCheckTargetSeqId,
    });
    setIsOpenCheckResultParaPointModal(true);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(ipCheckResultParamCondition);
    }
  };

  const handleOpenFileDownload = (atchFileGrId: string, atchFileTpCd: string) => {
    setFileUploadModalCondition({
      atchFileGrId: atchFileGrId,
      atchFileTpCd: atchFileTpCd,
      optValCtn1: 'TB_ELM_IP_CHECK_RESULT_D',
      bizName: 'ip',
      downloadOnly: true,
    });
    setOpenFileUploadModal(true);
  };

  const handleDownloadExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('ip.label.IP 점검결과', 'IP 점검결과');
    book.saveAsync(getExcelFileName(t('ip.label.IP점검결과조회', 'IP점검결과조회')));
  };
  const [flexRef, setFlexRef] = useState<any>();
  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'ipCheckPlanNo',
        header: String(t('ip.grid.점검계획번호', '점검계획번호')),
        width: 160,
        align: 'center',
      },
      {
        binding: 'ipCheckPlanName',
        header: String(t('ip.label.IP점검계획명', 'IP점검계획명')),
        width: 230,
        align: 'left',
      },
      {
        binding: 'ipCheckStatusNm',
        header: String(t('com.label.진행상태', '진행상태')),
        width: 115,
        align: 'center',
      },
      {
        header: String(t('com.label.설비군', '설비군')),
        columns: [
          {
            binding: 'equipmentGroup',
            header: ' ',
            width: 68,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ip.grid.설비', '설비')),
        //  collapseTo: 'machineName',
        // isCollapsed: true,
        columns: [
          {
            binding: 'electrodeTypeNm',
            header: String(t('ip.label.Anode/Cathode', 'Anode/Cathode')),
            width: 120,
            align: 'center',
          },
          {
            binding: 'eqpMain',
            header: String(t('ip.grid.Main', 'Main')),
            width: 120,
            align: 'left',
          },
          {
            binding: 'machineName',
            header: String(t('ip.label.Machine', 'Machine')),
            width: 120,
            align: 'left',
          },
          {
            binding: 'unitName',
            header: String(t('ip.grid.Unit', 'Unit')),
            width: 120,
            align: 'left',
          },
          {
            binding: 'assemblyName',
            header: String(t('ip.label.Assembly', 'Assembly')),
            width: 120,
            align: 'left',
          },
        ],
      },
      {
        header: String(t('ip.grid.IP 구분', 'IP 구분')),
        columns: [
          {
            binding: 'ipCategoryNm',
            header: ' ',
            width: 68,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ip.grid.점검단계', '점검단계')),
        columns: [
          {
            binding: 'ipCheckStepNm',
            header: ' ',
            width: 77,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ip.grid.파라미터 항목', '파라미터 항목')),
        columns: [
          {
            binding: 'paraItemNm',
            header: ' ',
            width: 90,
            align: 'center',
          },
        ],
      },
      {
        binding: 'paraId',
        header: String(t('ip.grid.파라미터ID', '파라미터ID')),
        width: 125,
        align: 'center',
      },
      {
        binding: 'paraName',
        header: String(t('ip.grid.파라미터명', '파라미터명')),
        width: 180,
        align: 'left',
      },
      {
        binding: 'paraDesc',
        header: String(t('ip.grid.파라미터 설명', '파라미터 설명')),
        width: 250,
        align: 'left',
      },
      {
        header: String(t('ip.grid.사양', '사양')),
        //  collapseTo: 'spec',
        // isCollapsed: true,
        columns: [
          {
            binding: 'specDataum',
            header: String(t('ip.grid.측정기준', '측정기준')),
            width: 150,
            align: 'left',
          },
          {
            binding: 'meterTypeNm',
            header: String(t('ip.grid.미터유형', '미터유형')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'lowerBound',
            header: String(t('ip.grid.하한', '하한')),
            width: 80,
            align: 'right',
          },
          {
            binding: 'upperBound',
            header: String(t('ip.grid.상한', '상한')),
            width: 80,
            align: 'right',
          },
          {
            binding: 'uomNm',
            header: String(t('ip.grid.UOM', 'UOM')),
            width: 80,
            align: 'center',
          },
          {
            binding: 'rmk',
            header: String(t('ip.grid.주석', '주석')),
            width: 80,
            align: 'left',
          },
        ],
      },
      {
        header: String(t('ip.grid.측정', '측정')),
        //   collapseTo: 'measure',
        //   isCollapsed: true,
        columns: [
          {
            binding: 'measureToolNm',
            header: String(t('ip.grid.도구', '도구')),
            width: 120,
            align: 'left',
          },
          {
            binding: 'measureMethod',
            header: String(t('ip.grid.방법', '방법')),
            width: 140,
            align: 'left',
          },
          {
            binding: 'measureGuide',
            header: String(t('ip.grid.가이드', '가이드')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'digitalTypeNm',
            header: String(t('ip.grid.A/D', 'A/D')),
            width: 80,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ip.grid.측정개소', '측정개소')),
        //   collapseTo: 'paraPointCnt',
        //   isCollapsed: true,
        columns: [
          {
            binding: 'paraPointType',
            header: String(t('com.label.유형', '유형')),
            width: 94,
            align: 'center',
            cssClass: 'WijmoPopup',
            cellTemplate: (params) => {
              if (params.value == null || params.value == '') {
                return '';
              } else {
                return `${params.item.paraPointTypeNm}${
                  params.value === 'POINT' && params.item.paraPointCnt > 0 ? ' <Button />' : ''
                }`;
              }
            },
          },
          {
            binding: 'paraPointCnt',
            header: String(t('ip.grid.개수', '개수')),
            width: 80,
            align: 'right',
          },
        ],
      },
      {
        binding: 'guideAtchFileCnt',
        header: String(t('com.label.첨부', '첨부')),
        width: 100,
        align: 'center',
        cellTemplate: (params) =>
          params.item.guideAtchFileGrId && params.value > 0
            ? `<div class="fileDiv"><span><em>${(
                params.value || 0
              ).toLocaleString()}</em></span></div>`
            : '0',
      },
      {
        header: String(t('ip.grid.CTQ/CTP', 'CTQ/CTP')),
        //   collapseTo: 'ctqctpTypeNm',
        //  isCollapsed: true,
        columns: [
          {
            binding: 'ctqctpTypeNm',
            header: String(t('com.label.유형', '유형')),
            width: 80,
            align: 'center',
          },
          {
            binding: 'ctqctpDetail',
            header: String(t('com.label.상세', '상세')),
            width: 80,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ip.grid.핵심IP여부', '핵심IP여부')),
        columns: [
          {
            binding: 'mainIpFlag',
            header: ' ',
            width: 76,
            align: 'center',
          },
        ],
      },
      {
        header: String(t('ip.grid.FAT Maker', 'FAT Maker')),
        //   collapseTo: 'fatMakerTargetFlag',
        //   isCollapsed: true,
        columns: [
          {
            binding: 'fatMakerTargetFlag',
            header: String(t('ip.grid.대상여부', '대상여부')),
            width: 110,
            align: 'center',
          },
          {
            binding: 'fatMakerValue',
            header: String(t('ip.grid.Value', 'Value')),
            width: 100,
            align: 'right',
          },
          {
            binding: 'fatMakerResult',
            header: String(t('ip.grid.Result', 'Result')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'fatMakerInspectorNm',
            header: String(t('ip.grid.점검자', '점검자')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'fatMakerComment',
            header: String(t('ip.grid.Comment', 'Comment')),
            width: 120,
            align: 'left',
          },
          {
            binding: 'fatMakerAtchFileCnt',
            header: String(t('com.label.첨부', '첨부')),
            width: 100,
            align: 'center',
            cellTemplate: (params) =>
              params.item.paraPointType === 'ATTACH' && params.value > 0
                ? `<div class="fileDiv"><span><em>${(
                    params.value || 0
                  ).toLocaleString()}</em></span></div>`
                : '',
          },
        ],
      },
      {
        header: String(t('ip.grid.FAT 엔솔', 'FAT 엔솔')),
        //    collapseTo: 'fatEnsolTargetFlag',
        //    isCollapsed: true,
        columns: [
          {
            binding: 'fatEnsolTargetFlag',
            header: String(t('ip.grid.대상여부', '대상여부')),
            width: 110,
            align: 'center',
          },
          {
            binding: 'fatEnsolValue',
            header: String(t('ip.grid.Value', 'Value')),
            width: 100,
            align: 'right',
          },
          {
            binding: 'fatEnsolResult',
            header: String(t('ip.grid.Result', 'Result')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'fatEnsolInspectorNm',
            header: String(t('ip.grid.점검자', '점검자')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'fatEnsolComment',
            header: String(t('ip.grid.Comment', 'Comment')),
            width: 120,
            align: 'left',
          },
          {
            binding: 'fatEnsolAtchFileCnt',
            header: String(t('com.label.첨부', '첨부')),
            width: 100,
            align: 'center',
            cellTemplate: (params) =>
              params.item.paraPointType === 'ATTACH' && params.value > 0
                ? `<div class="fileDiv"><span><em>${(
                    params.value || 0
                  ).toLocaleString()}</em></span></div>`
                : '',
          },
        ],
      },
      {
        header: String(t('ip.grid.SAT Maker', 'SAT Maker')),
        //   collapseTo: 'satMakerTargetFlag',
        //  isCollapsed: true,
        columns: [
          {
            binding: 'satMakerTargetFlag',
            header: String(t('ip.grid.대상여부', '대상여부')),
            width: 110,
            align: 'center',
          },
          {
            binding: 'satMakerValue',
            header: String(t('ip.grid.Value', 'Value')),
            width: 100,
            align: 'right',
          },
          {
            binding: 'satMakerResult',
            header: String(t('ip.grid.Result', 'Result')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'satMakerInspectorNm',
            header: String(t('ip.grid.점검자', '점검자')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'satMakerComment',
            header: String(t('ip.grid.Comment', 'Comment')),
            width: 120,
            align: 'left',
          },
          {
            binding: 'satMakerAtchFileCnt',
            header: String(t('com.label.첨부', '첨부')),
            width: 100,
            align: 'center',
            cellTemplate: (params) =>
              params.item.paraPointType === 'ATTACH' && params.value > 0
                ? `<div class="fileDiv"><span><em>${(
                    params.value || 0
                  ).toLocaleString()}</em></span></div>`
                : '',
          },
        ],
      },
      {
        header: String(t('ip.grid.SAT 엔솔', 'SAT 엔솔')),
        //   collapseTo: 'satEnsolTargetFlag',
        //  isCollapsed: true,
        columns: [
          {
            binding: 'satEnsolTargetFlag',
            header: String(t('ip.grid.대상여부', '대상여부')),
            width: 110,
            align: 'center',
          },
          {
            binding: 'satEnsolValue',
            header: String(t('ip.grid.Value', 'Value')),
            width: 100,
            align: 'right',
          },
          {
            binding: 'satEnsolResult',
            header: String(t('ip.grid.Result', 'Result')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'satEnsolInspectorNm',
            header: String(t('ip.grid.점검자', '점검자')),
            width: 100,
            align: 'center',
          },
          {
            binding: 'satEnsolComment',
            header: String(t('ip.grid.Comment', 'Comment')),
            width: 120,
            align: 'left',
          },
          {
            binding: 'satEnsolAtchFileCnt',
            header: String(t('com.label.첨부', '첨부')),
            width: 100,
            align: 'center',
            cellTemplate: (params) =>
              params.item.paraPointType === 'ATTACH' && params.value > 0
                ? `<div class="fileDiv"><span><em>${(
                    params.value || 0
                  ).toLocaleString()}</em></span></div>`
                : '',
          },
        ],
      },
    ];
  }, [isChangeColumn, flexRef]);

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if ('paraPointType' === binding) {
        if (item.paraPointType !== 'POINT' || item.paraPointCnt < 1) {
          removeClass(cell, 'WijmoPopup');
        }
      }
    }
  });
  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;
    setFlexRef(gridRef);
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });

      grid.invalidate();
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const binding = grid.columns[ht.col].binding;
      const item = grid.rows[ht.row].dataItem;

      if (ht.panel === grid.cells) {
        if ('paraPointType' === binding) {
          if (item.paraPointType === 'POINT' && item.paraPointCnt > 0) {
            handleParaPointTypeNmClick(item);
          }
        } else if ('guideAtchFileCnt' === binding) {
          if (item.guideAtchFileGrId && item.guideAtchFileCnt > 0) {
            setFileUploadModalCondition({
              atchFileGrId: item.guideAtchFileGrId,
              atchFileTpCd: 'NORMAL',
              optValCtn1: 'TB_ELM_IP_EQP_PARA_D',
              bizName: 'ip',
              downloadOnly: true,
            });
            setOpenFileUploadModal(true);
          }
        } else if ('fatMakerAtchFileCnt' === binding) {
          if (item.paraPointType === 'ATTACH' && item.fatMakerAtchFileCnt > 0) {
            handleOpenFileDownload(item.atchFileGrId, 'FAT_M_RST');
          }
        } else if ('fatEnsolAtchFileCnt' === binding) {
          if (item.paraPointType === 'ATTACH' && item.fatEnsolAtchFileCnt > 0) {
            handleOpenFileDownload(item.atchFileGrId, 'FAT_E_RST');
          }
        } else if ('satMakerAtchFileCnt' === binding) {
          if (item.paraPointType === 'ATTACH' && item.satMakerAtchFileCnt > 0) {
            handleOpenFileDownload(item.atchFileGrId, 'SAT_M_RST');
          }
        } else if ('satEnsolAtchFileCnt' === binding) {
          if (item.paraPointType === 'ATTACH' && item.satEnsolAtchFileCnt > 0) {
            handleOpenFileDownload(item.atchFileGrId, 'SAT_E_RST');
          }
        }
      }
    });
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{String(t('ip.label.IP점검계획번호', 'IP점검계획번호'))}</label>
                <CustomInputWithSearch
                  type="text"
                  name="ipCheckPlanNo"
                  placeholder={String(
                    t('ip.msg.IP 점검계획번호를 입력해 주세요.', 'IP 점검계획번호를 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.ipCheckPlanNo}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('ip.label.IP점검계획명', 'IP점검계획명'))}</label>
                <CustomInputWithSearch
                  type="text"
                  name="ipCheckPlanName"
                  placeholder={String(
                    t('ip.msg.IP 점검 계획명을 입력해 주세요.', 'IP 점검 계획명을 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.ipCheckPlanName}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ip.label.진행상태', '진행상태')}</label>
                <ComboBox
                  placeholder={String(
                    t('ip.msg.진행상태를 선택해 주세요.', '진행상태를 선택해 주세요.')
                  )}
                  options={ipStatusCodes}
                  defaultValue={ipCheckResultParamCondition.ipCheckStatus}
                  onChange={(value: string) =>
                    setIpCheckResultParamCondition({
                      ...ipCheckResultParamCondition,
                      ipCheckStatus: value,
                    })
                  }
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('ip.label.법인코드', '법인코드')}</label>
                <ComboBox
                  placeholder={String(
                    t('ip.msg.법인코드를 선택해 주세요.', '법인코드를 선택해 주세요.')
                  )}
                  options={copCodes}
                  defaultValue={ipCheckResultParamCondition.copCd}
                  onChange={(value: string) =>
                    setIpCheckResultParamCondition({
                      ...ipCheckResultParamCondition,
                      copCd: value,
                    })
                  }
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.설비군', '설비군')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="equipmentGroup"
                  className="find"
                  placeholder={String(
                    t('ip.msg.설비군을 입력해 주세요.', '설비군을 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.equipmentGroup}
                  onChange={handleParamConditionChange}
                  onSearchClick={() => setOpenEequipmentGroupPopUp(true)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.설비명', '설비명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="equipmentName"
                  placeholder={String(
                    t('ip.msg.설비명을 입력해 주세요.', '설비명을 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.equipmentName}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('ip.label.Machine', 'Machine')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="machineName"
                  placeholder={String(
                    t('ip.msg.Machine을 입력해 주세요.', 'Machine을 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.machineName}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ip.label.Assembly', 'Assembly')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="assemblyName"
                  placeholder={String(
                    t('ip.msg.Assembly를 입력해 주세요.', 'Assembly를 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.assemblyName}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ip.label.점검유형', '점검유형')}</label>
                <ComboBox
                  placeholder={String(
                    t('ip.label.점검유형', '점검유형') +
                      t('com.label.을 선택해 주세요.', '을 선택해 주세요.')
                  )}
                  options={isIsptTpCd}
                  defaultValue={ipCheckResultParamCondition.isptTpCd}
                  onChange={(value: string) =>
                    setIpCheckResultParamCondition({
                      ...ipCheckResultParamCondition,
                      isptTpCd: value,
                    })
                  }
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchBoxButtons
            setReset={setIpCheckResultParamCondition}
            onReset={() => {
              setDisplayCondition(
                (prev) =>
                  Object.keys(prev).reduce(
                    (acc, cur) => Object.assign(acc, { [cur]: '' }),
                    {}
                  ) as DisplayCondition
              );
            }}
            onSearch={() => handleSearch(ipCheckResultParamCondition)}
            onFolding={(isOpen) => setOpenSearchBox(isOpen)}
          />
        </SearchBoxRow>
        <SearchBoxRow style={{ display: isOpenSearchBox ? 'block' : 'none' }}>
          <InputBox>
            <SearchRows>
              <SearchCols>
                <label>{t('ip.label.설비파라미터번호', '설비 파라미터번호')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="eqpParaNo"
                  placeholder={String(
                    t(
                      'ip.msg.설비파라미터번호를 입력해 주세요.',
                      '설비파라미터번호를 입력해 주세요.'
                    )
                  )}
                  value={ipCheckResultParamCondition.eqpParaNo}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ip.label.설비파라미터명', '설비파라미터명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="eqpParaName"
                  placeholder={String(
                    t('ip.msg.설비 파라미터명을 입력해 주세요.', '설비 파라미터명을 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.eqpParaName}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ip.label.파라미터 항목', '파라미터 항목')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="paraItemNm"
                  className="find"
                  placeholder={String(
                    t('ip.msg.파라미터 항목을 입력해 주세요.', '파라미터 항목을 입력해 주세요.')
                  )}
                  value={displayCondition.paraItemNm}
                  onChange={handleDisplayConditionChange}
                  onSearchClick={() => setOpenParaItemPopUp(true)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('ip.label.파라미터명', '파라미터명')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="paraName"
                  placeholder={String(
                    t('ip.msg.파라미터명을 입력해 주세요.', '파라미터명을 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.paraName}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ip.label.Unit', 'Unit')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="unitName"
                  placeholder={String(
                    t('ip.msg.Unit 명을 입력해 주세요.', 'Unit 명을 입력해 주세요.')
                  )}
                  value={ipCheckResultParamCondition.unitName}
                  onChange={handleParamConditionChange}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('ip.label.Maker', 'Maker')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="ptnrNm"
                  className="find"
                  placeholder={String(
                    t('ip.msg.협력사 정보를 입력해 주세요.', '협력사 정보를 입력해 주세요.')
                  )}
                  value={displayCondition.ptnrNm}
                  onChange={handleDisplayConditionChange}
                  onSearchClick={() => setIsOpenPartnerModal(true)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows>
              <SearchCols>
                <label>{t('ip.label.Maker 점검자', 'Maker 점검자')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="makerInspectorNm"
                  className="find"
                  placeholder={String(
                    t('ip.msg.Maker 점검자를 입력해 주세요.', 'Maker 점검자를 입력해 주세요.')
                  )}
                  value={displayCondition.makerInspectorNm}
                  onChange={handleDisplayConditionChange}
                  onSearchClick={() => setOpenPartnerUserModal(true)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('com.label.엔솔 점검자', '엔솔 점검자')}</label>
                <CustomInputWithSearch
                  type="text"
                  name="ensolInspectorNm"
                  className="find"
                  placeholder={String(
                    t('ip.msg.엔솔 점검자를 입력해 주세요.', '엔솔 점검자를 입력해 주세요.')
                  )}
                  value={displayCondition.ensolInspectorNm}
                  onChange={handleDisplayConditionChange}
                  onSearchClick={() => setOpenElmEmpPopup(true)}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('ip.label.IP 점검결과', 'IP 점검결과')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleDownloadExcel}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch(ipCheckResultParamCondition)}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>

          <GridSettingButtons
            gridId={'checkReulstPageGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              //openLoading(true);
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              gridRef.current.refresh();
              //openLoading(false);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'checkReulstPageGrid'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={520}
        isSelector={false}
        isReadOnly={true}
        initialized={onInitialized}
      />

      {isOpenCheckResultParaPointModal && (
        <CheckResultParaPointModal
          condition={selectedRow}
          close={() => setIsOpenCheckResultParaPointModal(false)}
        />
      )}

      {isOpenEquipmentGroupPopUp && (
        <EquipmentGroupPopUp
          open={isOpenEquipmentGroupPopUp}
          close={() => setOpenEequipmentGroupPopUp(false)}
          condition={{
            equipmentGroup: ipCheckResultParamCondition.equipmentGroup || '',
          }}
          onCallback={(value) =>
            setIpCheckResultParamCondition({
              ...ipCheckResultParamCondition,
              equipmentGroup: (value as EquipmentGroupCondition).equipmentGroup,
            })
          }
        />
      )}

      {isOpenParaItemPopUp && (
        <ParaItemPopUp
          open={isOpenParaItemPopUp}
          close={() => setOpenParaItemPopUp(false)}
          condition={{
            paraItemNm: displayCondition.paraItemNm || '',
          }}
          onCallback={(value) => {
            setIpCheckResultParamCondition({
              ...ipCheckResultParamCondition,
              paraItem: (value as ParaItemCondition).paraItem,
            });
            setDisplayCondition({
              ...displayCondition,
              paraItemNm: (value as ParaItemCondition).paraItemNm,
            });
          }}
        />
      )}

      {isOpenPartnerUserModal && (
        <PartnerUserModal
          open={isOpenPartnerUserModal}
          condition={{
            userNm: displayCondition.makerInspectorNm || '',
          }}
          close={() => setOpenPartnerUserModal(false)}
          onCallback={(value) => {
            setIpCheckResultParamCondition({
              ...ipCheckResultParamCondition,
              makerInspector: (value as ExcpPartner).userId,
            });
            setDisplayCondition({
              ...displayCondition,
              makerInspectorNm: (value as ExcpPartner).userNm,
            });
          }}
        />
      )}

      {isOpenPartnerModal && (
        <PartnerModal
          open={isOpenPartnerUserModal}
          condition={{
            excpNm: displayCondition.ptnrNm || '',
          }}
          onCallback={(value) => {
            setIpCheckResultParamCondition({
              ...ipCheckResultParamCondition,
              makerCd: (value as ExcpPartner).excpId,
            });
            setDisplayCondition({
              ...displayCondition,
              ptnrNm: (value as ExcpPartner).excpNm,
            });
          }}
          close={() => setIsOpenPartnerModal(false)}
        />
      )}

      {isOpenElmEmpPopup && (
        <ElmEmpPopup
          setUserData={setUserData}
          setPopup={setOpenElmEmpPopup}
          initParam={displayCondition.ensolInspectorNm}
        />
      )}

      {isOpenFileUploadModal && (
        <FileUploadPopUp
          open={isOpenFileUploadModal}
          close={() => setOpenFileUploadModal(false)}
          downloadOnly={fileUploadModalCondition.downloadOnly}
          initParam={{
            atchFileGrId: fileUploadModalCondition.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition.atchFileTpCd,
            optValCtn1: fileUploadModalCondition.tableName,
            bizName: fileUploadModalCondition.bizName,
          }}
          onCallback={() => {
            setOpenFileUploadModal(false);
          }}
        />
      )}
    </>
  );
};

export default CheckResultPage;
